import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Eye, ArrowRight, ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VesperSequence from "@/components/VesperSequence";
import "./mieruka-login-layout.css";

export default function LoginPage() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const root = document.getElementById("root");
    document.documentElement.classList.add("mk-host");
    document.body.classList.add("mk-host");
    root?.classList.add("mk-host");
    return () => {
      document.documentElement.classList.remove("mk-host");
      document.body.classList.remove("mk-host");
      root?.classList.remove("mk-host");
    };
  }, []);

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email: email.trim() });
      setStep("otp");
      setOtp("");
      toast.success("Access code sent to your email");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email: email.trim(), otp: code });
      login(data.token, data.user);
      toast.success(`Welcome, ${data.user.name.split(" ")[0]}`);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
      setOtp("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mk-login" data-testid="login-page">
      <VesperSequence />

      <div className="mk-column" data-testid="login-column">
        <div className="mk-brand">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[rgb(var(--th-amber))] shadow-[0_0_24px_rgba(251,191,36,0.3)]">
            <Eye className="h-6 w-6 text-[#071126]" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-[rgb(var(--th-strong))]">Mieruka</h1>
            <p className="font-mono-hud text-[10px] uppercase tracking-[0.3em] text-[rgb(var(--th-amber))]">見える化 · Make it visible</p>
          </div>
        </div>

        <div className="mk-form">
          <AnimatePresence mode="wait">
            {step === "email" ? (
              <motion.form
                key="email"
                onSubmit={requestOtp}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25 }}
              >
                <label htmlFor="login-email" className="font-mono-hud mb-3 block text-[11px] uppercase tracking-[0.25em] text-[rgb(var(--th-mute))]">
                  Identify · Email
                </label>
                <input
                  data-testid="login-email-input"
                  id="login-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoFocus
                  className="font-mono-hud w-full border-b-2 border-[rgba(160,196,255,0.25)] bg-transparent px-1 py-3 text-lg text-[rgb(var(--th-strong))] placeholder:text-[rgb(var(--th-faint))] focus:border-[rgb(var(--th-amber))] focus:outline-none"
                />
                <button
                  data-testid="login-request-otp-btn"
                  type="submit"
                  disabled={busy || !email.trim()}
                  className="mt-8 flex w-full items-center justify-center gap-2 rounded-md bg-[rgb(var(--th-amber))] py-3.5 font-heading font-semibold text-[#071126] transition-colors hover:bg-[#f0b323] disabled:opacity-40 disabled:hover:bg-[rgb(var(--th-amber))]"
                >
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Request Access Code <ArrowRight className="h-4 w-4" /></>}
                </button>
                <p className="font-mono-hud mt-5 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[rgb(var(--th-mute))]">
                  <ShieldCheck className="h-3.5 w-3.5 text-[rgb(var(--th-mint))]" /> Authorised personnel only
                </p>
              </motion.form>
            ) : (
              <motion.div
                key="otp"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 16 }}
                transition={{ duration: 0.25 }}
              >
                <label className="font-mono-hud mb-1 block text-[11px] uppercase tracking-[0.25em] text-[rgb(var(--th-mute))]">
                  Verify · Access Code
                </label>
                <p className="mb-6 text-sm text-[rgb(var(--th-mute))]">
                  Code sent to <span className="font-mono-hud text-[rgb(var(--th-amber))]">{email}</span>
                </p>
                <div data-testid="login-otp-input" className="flex justify-start">
                  <InputOTP maxLength={6} value={otp} onChange={(v) => { setOtp(v); if (v.length === 6) verifyOtp(v); }}>
                    <InputOTPGroup className="gap-2">
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot
                          key={i}
                          index={i}
                          className="h-12 w-11 rounded-md border border-[rgba(160,196,255,0.2)] bg-[rgba(7,17,38,0.8)] font-mono-hud text-lg text-[rgb(var(--th-amber))] first:rounded-l-md last:rounded-r-md"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {busy && (
                  <p className="font-mono-hud mt-4 flex items-center gap-2 text-xs text-[rgb(var(--th-amber))]">
                    <Loader2 className="h-4 w-4 animate-spin" /> VERIFYING...
                  </p>
                )}
                <div className="mt-7 flex items-center justify-between">
                  <button
                    data-testid="login-back-btn"
                    onClick={() => setStep("email")}
                    className="flex items-center gap-1.5 text-sm text-[rgb(var(--th-mute))] transition-colors hover:text-[rgb(var(--th-strong))]"
                  >
                    <ArrowLeft className="h-4 w-4" /> Change email
                  </button>
                  <button
                    data-testid="login-resend-btn"
                    onClick={() => requestOtp()}
                    disabled={busy}
                    className="text-sm text-[rgb(var(--th-amber))] transition-colors hover:text-[#f0b323] disabled:opacity-40"
                  >
                    Resend code
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p data-testid="login-footer" className="mk-footer font-mono-hud text-[10px] uppercase tracking-[0.25em] text-[rgb(var(--th-dim))]">
          Zefficacy © 2026 · Mieruka Platform
        </p>
      </div>
    </div>
  );
}
